<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<crucible-dialog
  [form]="form"
  [loading]="isSaving"
  [guardUnsavedWork]="form.dirty"
  [hideDefaultActions]="true"
  (submit)="submit()"
  (cancel)="cancel()"
>
  <div
    crucibleDialogTitle
    class="d-flex flex-row justify-content-start align-items-center"
  >
    @if (readOnly) {
      <mat-icon
        class="mdi-24px"
        fontIcon="mdi-alert-octagon"
        matTooltip="Read-only because the current file is not in edit mode or you do not have edit permissions"
        matTooltipShowDelay="0"
      ></mat-icon>
    }
    @if (promptForReload) {
      <button
        mat-icon-button
        aria-label="Reload module"
        matTooltip="Reload this module to view changes. Unsaved changes will be lost."
        matTooltipShowDelay="0"
        (click)="reload()"
      >
        <mat-icon class="mdi-24px" fontIcon="mdi-alert-circle"> </mat-icon>
      </button>
    }
    <span [ngClass]="promptForReload ? 'alert' : ''">
      {{ selectedModule.name }}
    </span>
  </div>

  <div crucibleDialogContent [formGroup]="form">
    <p class="module-path">{{ selectedModule.path }}</p>
    <p class="module-description">{{ selectedModule.description }}</p>
    <mat-form-field>
      <mat-label>Version</mat-label>
      <mat-select
        formControlName="selectedVersion"
        (selectionChange)="onSelectedVersionChanged($event.value)"
      >
        @for (version of selectedModule.versions; track version) {
          <mat-option [value]="version">
            {{ version.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <div class="flex-grow-1 d-flex flex-column module-fields">
      <mat-card appearance="outlined">
        <mat-form-field>
          <mat-label [ngClass]="nameChanged ? 'changed-hint' : ''"
            >Name of this instance</mat-label
          >
          <input
            matInput
            placeholder="Name of this module instance"
            formControlName="newName"
          />
          @if (form.get('newName').invalid) {
            <mat-error>
              Must start with a letter or _, then letters, numbers, - and _ only
            </mat-error>
          }
          @if (nameChanged) {
            <mat-hint
              align="end"
              class="changed-hint"
              [matTooltip]="'Click to accept new name:\n' + newName"
              [matTooltipShowDelay]="0"
              (click)="acceptName()"
              >Changed</mat-hint
            >
          }
        </mat-form-field>
      </mat-card>
      <ng-container formGroupName="values">
        @for (moduleField of moduleFields; track moduleField) {
          <mat-card appearance="outlined">
            <mat-form-field>
              @if (!moduleField.isOptional) {
                <mat-hint>Required</mat-hint>
              }
              @if (moduleField.changed) {
                <mat-hint
                  align="end"
                  class="changed-hint"
                  [matTooltip]="'Click to accept new value:\n' + moduleField.value"
                  [matTooltipShowDelay]="0"
                  (click)="acceptValue(moduleField)"
                  >Changed</mat-hint
                >
              }
              <mat-label [ngClass]="moduleField.changed ? 'changed-hint' : ''">{{
                moduleField.name
              }}</mat-label>
              @if (!moduleField.isMultiLine()) {
                <input
                  matInput
                  placeholder="{{ moduleField.name }}"
                  matTooltip="{{ moduleField.description }}"
                  formControlName="{{ moduleField.name }}"
                />
              } @else {
                <textarea
                  matInput
                  placeholder="{{ moduleField.name }}"
                  matTooltip="{{ moduleField.description }}"
                  cdkTextareaAutosize
                  #autosize="cdkTextareaAutosize"
                  formControlName="{{ moduleField.name }}"
                  spellcheck="false"
                ></textarea>
              }
              @if (form.get(['values', moduleField.name]).hasError('required')) {
                <mat-error>Required</mat-error>
              }
              @if (variables?.length > 0 || outputs?.length > 0) {
                <button
                  type="button"
                  color="primary"
                  mat-icon-button
                  (click)="
                    trigger.panelOpen ? trigger.closePanel() : trigger.openPanel()
                  "
                  aria-label="View variables and outputs"
                  matTooltip="View variables and outputs"
                  matSuffix
                >
                  <mat-icon class="mdi-16px" fontIcon="mdi-text"> </mat-icon>
                </button>
              }
              <input
                class="hidden"
                [matAutocomplete]="autoGroup"
                #trigger="matAutocompleteTrigger"
              />
              <mat-autocomplete
                #autoGroup
                (optionSelected)="autocompleteSelected(moduleField.name, $event)"
              >
                @if (variables?.length > 0) {
                  <mat-optgroup label="Variables">
                    @for (variable of variables; track variable) {
                      <mat-option [value]="variable.terraform">
                        {{ variable.name }}
                      </mat-option>
                    }
                  </mat-optgroup>
                }
                @if (outputs?.length > 0) {
                  <mat-optgroup label="Outputs">
                    @for (output of outputs; track output) {
                      <mat-option [value]="output.terraform">
                        {{ output.designModuleName }}.{{ output.name }}
                      </mat-option>
                    }
                  </mat-optgroup>
                }
              </mat-autocomplete>
            </mat-form-field>
          </mat-card>
        }
      </ng-container>
    </div>
  </div>

  <ng-container crucibleDialogActions>
    <button matButton="outlined" type="button" (click)="cancel()">
      @if (!form.dirty) {
        Close
      } @else {
        Cancel
      }
    </button>
    <button
      matButton="filled"
      type="submit"
      [disabled]="readOnly || !form.valid || (requireChanges && !form.dirty) || isSaving"
    >
      <span [matTooltip]="readOnly ? 'Can only submit in edit mode' : null">
        {{ isSaving ? 'Saving…' : submitButtonText }}
      </span>
    </button>
  </ng-container>
</crucible-dialog>
